import { createContext, useEffect, useReducer, useState } from "react";
import axios from "axios";
import { useTenant } from "../hooks";
const JournalEntryContext = createContext();
const DEFAULT_PER_PAGE = 15;

const initialState = {
  entries: [],
  pagination: {
    current_page: 1,
    last_page: 1,
    per_page: DEFAULT_PER_PAGE,
    total: 0,
  },
};
const resolveDetailAccountNumber = (detail) =>
  String(detail?.account_no || "").trim();

const resolveDetailAccountName = (detail) =>
  String(detail?.tree_relation?.name_ar || "").trim();

const resolveDetailCostCenterNumber = (detail) =>
  String(detail?.cost_center_no || "").trim();

const resolveDetailCostCenterName = (detail) =>
  String(detail?.treecost_relation?.name_ar || "").trim();

const normalizeDetail = (detail) => {
  const accountNumber = resolveDetailAccountNumber(detail);
  const accountName = resolveDetailAccountName(detail);
  const costCenterNumber = resolveDetailCostCenterNumber(detail);
  const costCenterLabel = resolveDetailCostCenterName(detail);

  return {
    ...detail,
    account_no: accountNumber,
    accountName: accountName.trim(),
    name: accountName.trim(),
    cost_center_no: costCenterNumber,
    costCenterLabel: costCenterLabel.trim(),
    tree_relation: detail?.tree_relation || null,
    treecost_relation: detail?.treecost_relation || null,
    trans_de: Number(detail.trans_de || 0),
    trans_cr: Number(detail.trans_cr || 0),
  };
};

const normalizeEntry = (entry = {}) => {
  const rawDetails = Array.isArray(entry.detail_relation)
    ? entry.detail_relation
    : Array.isArray(entry.details)
      ? entry.details
      : [];

  return {
    ...entry,
    details: rawDetails.map((detail) => normalizeDetail(detail)),
  };
};

const JournalEntryReducer = (state, action) => {
  switch (action.type) {
    case "SET_ENTRIES":
      return {
        ...state,
        entries: action.payload.data.map(normalizeEntry),
        pagination: action.payload.pagination,
      };

    case "ADD_ENTRY":
      return {
        ...state,
        entries: [...state.entries, normalizeEntry(action.payload)],
      };

    case "EDIT_ENTRY":
      return {
        ...state,
        entries: state.entries.map((entry) =>
          entry.id === action.payload.id
            ? normalizeEntry(action.payload)
            : entry,
        ),
      };

    case "DELETE_ENTRY":
      return {
        ...state,
        entries: state.entries.filter((entry) => entry.id !== action.payload),
      };

    default:
      return state;
  }
};

export const JournalEntryProvider = ({ children }) => {
  const [state, dispatch] = useReducer(JournalEntryReducer, initialState);
  const [loading, setLoading] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedPeriodDetails, setSelectedPeriodDetails] = useState(null);

  const baseurl = process.env.REACT_APP_INV_API_SERVER_BASE_URL;
  const { tenantId, companySelected, branchSelected } = useTenant();

  useEffect(() => {
    const period = JSON.parse(localStorage.getItem("selectedPeriod") || "{}");
    setSelectedPeriod(period?.id);
    setSelectedPeriodDetails({
      id: period?.id,
      label: period?.label,
      state: period?.state,
      is_current: period?.is_current,
      closeduntil: period?.closeduntil,
      is_closed: period?.is_closed,
      fin_status: period?.fin_status,
      startDateDisplay: period?.startDateDisplay,
      endDateDisplay: period?.endDateDisplay,
    });
  }, []);

  const isPeriodClosed = (() => {
    const period = selectedPeriodDetails;
    if (!period) return false;
    if (period.is_closed === true) return true;
    return false;
  })();

  const resolveEntryId = (target) => {
    if (target && typeof target === "object") {
      return target?.id ?? target?._id ?? null;
    }
    return target ?? null;
  };

  const resolveEntryIsPosted = (target) => {
    if (target && typeof target === "object") {
      return Number(target?.is_completed ?? 0) === 1;
    }
    const entryId = resolveEntryId(target);
    if (!entryId) return false;
    const match = state.entries.find(
      (entry) => String(entry.id || entry._id) === String(entryId),
    );
    return Number(match?.is_completed ?? 0) === 1;
  };

  const fetchJournalEntries = async (page = 1) => {
    try {
      setLoading(true);
      const perPage = DEFAULT_PER_PAGE;
      const response = await axios.post(
        `${baseurl}/transactions?page=${page}`,
        {
          fin_year_id: selectedPeriod,
          tenant_id: tenantId,
          company_id: companySelected?.value,
          branch_id: branchSelected?.value,
          per_page: perPage,
        },
      );
      console.log(response);
      
      const { transactions } = response.data.data;

      dispatch({
        type: "SET_ENTRIES",
        payload: {
          data: transactions.data,
          pagination: {
            current_page: transactions.current_page,
            last_page: transactions.last_page,
            per_page: transactions.per_page,
            total: transactions.total,
          },
        },
      });
    } catch (error) {
      console.error("❌ خطأ في جلب القيود:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const addEntry = async (payload) => {
    if (isPeriodClosed) {
      return {
        success: false,
        error: "الفترة المالية مغلقة ولا يمكن إضافة قيود جديدة",
      };
    }
    try {
      setLoadingSave(true);
      const response = await axios.post(
        `${baseurl}/transactions/store`,
        payload,
      );
      const newEntry = response.data.data;

      dispatch({
        type: "ADD_ENTRY",
        payload: newEntry,
      });
      fetchJournalEntries(1);
      return { success: true, data: newEntry };
    } catch (error) {
      const errorMsg = error.response?.data?.message || "خطأ في إضافة القيد";
      console.error("❌ خطأ في الإضافة:", errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoadingSave(false);
    }
  };

  const editEntry = async (payload) => {
    if (isPeriodClosed) {
      return {
        success: false,
        error: "الفترة المالية مغلقة ولا يمكن تعديل القيود",
      };
    }
    try {
      setLoadingSave(true);
      const response = await axios.post(
        `${baseurl}/transactions/store`,
        payload,
      );
      const updatedEntry = response.data.data;

      dispatch({
        type: "EDIT_ENTRY",
        payload: updatedEntry,
      });
      fetchJournalEntries(1);
      return { success: true, data: updatedEntry };
    } catch (error) {
      const errorMsg = error.response?.data?.message || "خطأ في تعديل القيد";
      console.error("❌ خطأ في التعديل:", errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoadingSave(false);
    }
  };

  const deleteEntry = async (target) => {
    const entryId = resolveEntryId(target);
    if (!entryId) {
      throw new Error("Missing journal entry id");
    }
    if (resolveEntryIsPosted(target)) {
      throw new Error("القيد مرحل ولا يمكن حذفه");
    }
    if (isPeriodClosed) {
      return {
        success: false,
        error: "الفترة المالية مغلقة ولا يمكن حذف القيود",
      };
    }
    try {
      setLoading(true);
      await axios.get(`${baseurl}/transactions/delete/${entryId}`);

      dispatch({
        type: "DELETE_ENTRY",
        payload: entryId,
      });
      fetchJournalEntries(1);

      return { success: true };
    } catch (error) {
      const errorMsg = error.response?.data?.message || "خطأ في حذف القيد";
      console.error("❌ خطأ في الحذف:", errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      !tenantId ||
      !companySelected?.value ||
      !branchSelected?.value ||
      !selectedPeriod
    ) {
      return;
    }

    fetchJournalEntries(1);
  }, [tenantId, companySelected?.value, branchSelected?.value, selectedPeriod]);
  useEffect(() => {
    const handlePeriodChange = () => {
      const period = JSON.parse(localStorage.getItem("selectedPeriod") || "{}");
      setSelectedPeriod(period?.id);
      setSelectedPeriodDetails({
        id: period?.id,
        label: period?.label,
        state: period?.state,
        is_current: period?.is_current,
        closeduntil: period?.closeduntil,
        is_closed: period?.is_closed,
        fin_status: period?.fin_status,
        startDateDisplay: period?.startDateDisplay,
        endDateDisplay: period?.endDateDisplay,
      });
    };

    window.addEventListener("periodChanged", handlePeriodChange);
    return () =>
      window.removeEventListener("periodChanged", handlePeriodChange);
  }, []);

  return (
    <JournalEntryContext.Provider
      value={{
        entries: state.entries,
        JournalEntries: state.entries,
        pagination: state.pagination,
        loading,
        selectedPeriod,
        selectedPeriodDetails,
        isPeriodClosed,
        fetchJournalEntries,
        loadingSave,
        addEntry,
        editEntry,
        deleteEntry,
        dispatch,
      }}
    >
      {children}
    </JournalEntryContext.Provider>
  );
};

export default JournalEntryContext;
